import { useT } from '../i18n/I18nProvider';
import type { ScreenModel } from '../domain/resolveScreen';
import { formatDate } from '../domain/resolveScreen';
import type { PackageInfo } from '../domain/types';
import { Icon } from './icons';
import { CompletionBand, TempBand } from './ScreenBlocks';

/** `27201` → `272 01`. Detail, ale nedůvěra v data začíná právě tady. */
function formatZip(zip: string | null): string | null {
  if (!zip) return null;
  const digits = zip.replace(/\s/g, '');
  return digits.length === 5 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : zip;
}

function mapsHref(pkg: PackageInfo): string {
  if (pkg.recipient.lat != null && pkg.recipient.lon != null) {
    return `https://www.google.com/maps/search/?api=1&query=${pkg.recipient.lat},${pkg.recipient.lon}`;
  }
  const parts = [pkg.recipient.street, pkg.recipient.city, formatZip(pkg.recipient.zip)]
    .filter(Boolean)
    .join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

interface Props {
  model: ScreenModel;
  pkg: PackageInfo;
  onEditWeight: () => void;
}

/**
 * Karta má tři jasně oddělené zóny, které se nedají splést:
 *   identifikace (bílá) → pick blok (tmavý) → adresát (bílá)
 */
export function ShipmentCard({ model, pkg, onEditWeight }: Props) {
  const { t } = useT();
  const zip = formatZip(pkg.recipient.zip);

  return (
    <div className="card">
      {/* --- zóna 1: identifikace --- */}
      <div className="card-top" style={{ paddingBottom: 12 }}>
        <div className="id-row">
          <span>
            <span className="id-lbl">{t('idLbl')}</span>
            {/* Číslo zásilky backend zatím neposílá – dokud nepřijde, ukazujeme
                naskenovaný kód, ať má skladník co porovnat se štítkem. */}
            <span className="id">{pkg.shipmentNumber ?? pkg.scannedCode}</span>
            {model.scanCountBadge && (
              <span className={`scans${model.scanCountBadge.warn ? ' dup' : ''}`}>
                <Icon name="scan" />
                {t('scannedBy')} <b>{model.scanCountBadge.count}×</b>
              </span>
            )}
          </span>
          {pkg.sequence != null && (
            <span className="seq">
              <b>{pkg.sequence}</b>
              <span>{t('seq')}</span>
            </span>
          )}
        </div>
      </div>

      {/* --- zóna 2: pick blok (podle něj se pickuje) --- */}
      {(pkg.pick.driver || pkg.pick.routeNumber || pkg.pick.vehicleName) && (
        <div className="pick">
          <div className="prow">
            {pkg.pick.driver && (
              <span className="pcol">
                <span className="k">{t('driver')}</span>
                <span className="pv">{pkg.pick.driver}</span>
              </span>
            )}
            {pkg.pick.routeNumber && (
              <span className="pcol rt">
                <span className="k">{t('routeNo')}</span>
                <span className="pv num">{pkg.pick.routeNumber}</span>
              </span>
            )}
          </div>
          {pkg.pick.vehicleName && (
            <div className="veh">
              <span className="k">{t('vehicle')}</span>
              <span className="pv">
                {pkg.pick.vehicleName}{' '}
                {pkg.pick.plate && <span className="plate">{pkg.pick.plate}</span>}
              </span>
            </div>
          )}
        </div>
      )}

      {/* --- zóna 3: adresát --- */}
      <div className="card-top" style={{ paddingTop: 13 }}>
        {pkg.recipient.name && <div className="who">{pkg.recipient.name}</div>}
        {pkg.recipient.note && <div className="who-sub">{pkg.recipient.note}</div>}
        {pkg.recipient.street && (
          <div className="addr">
            <Icon name="pin" />
            <div>
              {pkg.recipient.street}
              {(pkg.recipient.city || zip) && (
                <small>{[pkg.recipient.city, zip].filter(Boolean).join(' ')}</small>
              )}
            </div>
          </div>
        )}
        {model.actions.showNavigate && (
          <button
            className="maplink"
            type="button"
            onClick={() => window.open(mapsHref(pkg), '_blank', 'noopener')}
          >
            <Icon name="nav" />
            {t('nav')}
          </button>
        )}
      </div>

      <TempBand model={model} />
      <CompletionBand model={model} />

      <div className="grid">
        <div className="cell">
          <div className="k">{model.isMultiPackage ? t('pkgLbl') : t('count')}</div>
          <div className="v">
            {model.isMultiPackage && pkg.order.packageIndex != null
              ? `${pkg.order.packageIndex} ${t('ofN')} ${model.packageTotal}`
              : (model.packageTotal ?? 1)}
          </div>
        </div>

        <div className="cell">
          <div className="k">{t('weight')}</div>
          <div className="v edit">
            {/* Chybějící váha: „— kg“ a nabídka doplnit, ne prázdno. */}
            <span>{pkg.weightKg != null ? `${pkg.weightKg} kg` : t('noWeight')}</span>
            {model.actions.showEditWeight && (
              <button className="mini" type="button" onClick={onEditWeight}>
                {pkg.weightKg != null ? t('edit') : t('addWeight')}
              </button>
            )}
          </div>
        </div>

        {pkg.deliverAt && (
          <div className="cell">
            <div className="k">{t('date')}</div>
            <div className="v">{formatDate(pkg.deliverAt)}</div>
          </div>
        )}

        {pkg.zone && (
          <div className="cell">
            <div className="k">{t('zone')}</div>
            <div className="v">{pkg.zone}</div>
          </div>
        )}

        {pkg.deliveryTime && (
          <div className="cell full">
            <div className="k">{t('deliveryTime')}</div>
            <div className="v">{pkg.deliveryTime}</div>
          </div>
        )}
      </div>
    </div>
  );
}
